import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDaysIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatShortDate, formatTime, getTodayIso, parseDateOnly } from '@odo/shared-ui';
import { useAppointmentsMine } from '../../../hooks/useAppointments';
import type { Appointment } from '../../../services/appointments';
import {
    APPOINTMENT_PATIENT_QUERY_PARAM,
    APPOINTMENT_QUERY_PARAM
} from '../../appointments/AppointmentsModal';
import { APPOINTMENTS_QUERY_OPEN, APPOINTMENTS_QUERY_PARAM } from '../../../utils/constants';

const MAX_UPCOMING_APPOINTMENTS = 3;
const MS_PER_DAY = 86_400_000;

interface UpcomingAppointmentsCardProps {
    patientId: string | null;
}

/** Days between `dateOnly` and `todayIso` (positive = future). Given the
 *  `>= today` filter below, this is always >= 0. */
function daysUntil(dateOnly: string, todayIso: string): number {
    const diff = parseDateOnly(dateOnly).getTime() - parseDateOnly(todayIso).getTime();
    return Math.round(diff / MS_PER_DAY);
}

function dayCountdownLabel(diffDays: number): string {
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
}

interface AppointmentRowProps {
    appointment: Appointment;
    todayIso: string;
    onSelect: (appointmentId: string) => void;
}

/**
 * Solid-box tone for the countdown pill: Today = emerald, Tomorrow = amber,
 * further out = gray. Same 3-tone family the work-queue's status pills use
 * (see `ManufacturerStatusPill`'s per-status hash palette). This card only
 * ever shows non-canceled appointments dated today or later (see the
 * `>= today` filter below), so there is no "overdue" case to style here.
 */
function countdownTone(diffDays: number): string {
    if (diffDays <= 0) return 'bg-emerald-600';
    if (diffDays === 1) return 'bg-amber-600';
    return 'bg-gray-500';
}

/** A day-countdown chip ("Today" / "Tomorrow" / "In N days"), right-aligned
 *  so the reason/date block reads first. Styled like files-viewer's
 *  work-queue status pills (`ManufacturerStatusPill` / `TeamPill`, also
 *  mirrored by the dashboard's own billing `StatusBadge`): a solid-color
 *  box, white text, and a soft `bg-white/40` leading dot. The hour, when
 *  present, lives in the date line instead ("Aug 12, 2026 · 2:00 PM").
 *
 *  The row itself is a button, styled as its own little card (nested inside
 *  the outer `rounded-xl` panel, so `rounded-lg` here reads as "inside" it)
 *  with a trailing chevron as the clickability affordance. Clicking it
 *  opens the (globally mounted) appointments modal straight onto this
 *  appointment, the same `?appointments=open&appointment=<id>` deep-link
 *  contract NextAppointmentCell's next-appointment date link uses. */
function AppointmentRow({ appointment, todayIso, onSelect }: AppointmentRowProps) {
    const diffDays = daysUntil(appointment.date, todayIso);
    const countdownLabel = dayCountdownLabel(diffDays);
    const chipTone = countdownTone(diffDays);

    return (
        <button
            type="button"
            onClick={() => onSelect(appointment.id)}
            title={`View appointment: ${appointment.reason || 'No reason given'}`}
            className="flex items-center gap-3 p-3 w-full text-left bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:shadow-sm transition-all"
        >
            <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-medium text-gray-800 truncate">
                    {appointment.reason || 'No reason given'}
                </div>
                <div className="flex items-center gap-1 text-xs truncate text-gray-500">
                    <span className="truncate">
                        {formatShortDate(appointment.date)}
                        {appointment.time && ` · ${formatTime(appointment.time)}`}
                    </span>
                </div>
            </div>
            <div className="shrink-0 flex items-center gap-2">
                <span
                    className={`inline-flex items-center max-w-[100px] gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold text-white ${chipTone}`}
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
                    <span className="truncate">{countdownLabel}</span>
                </span>
                <ChevronRightIcon className="h-4 w-4 text-gray-400 shrink-0" />
            </div>
        </button>
    );
}

function EmptyState() {
    return (
        <div className="h-full flex flex-col items-center justify-center text-center gap-1.5">
            <div className="h-9 w-9 grid place-items-center rounded-full bg-gray-50 border border-gray-200 text-gray-400">
                <CalendarDaysIcon className="h-4 w-4" />
            </div>
            <span className="text-sm font-semibold text-gray-800">No upcoming appointments</span>
        </div>
    );
}

/** Display-only agenda preview (no booking flow here, v1). Filters the
 *  practice-wide `/appointments/mine` list down to this patient's
 *  non-canceled, not-yet-past appointments, soonest first, and renders each
 *  one with the same row/empty-state polish as the appointments modal's
 *  `ApptRow` / `AgendaEmptyState` so this card reads like a proper agenda
 *  rather than a bare list. */
function UpcomingAppointmentsCard({ patientId }: UpcomingAppointmentsCardProps) {
    const { appointments, isLoading } = useAppointmentsMine();
    const todayIso = getTodayIso();
    const [, setSearchParams] = useSearchParams();

    // Opens the globally mounted appointments modal (see
    // GlobalAppointmentsModal) straight onto this appointment - same
    // `?appointments=open&appointment=<id>` deep-link contract
    // NextAppointmentCell's openExistingAppointment uses, so both entry points
    // stay in sync with the modal's push-on-open / Back-closes behavior.
    const openExistingAppointment = (appointmentId: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(APPOINTMENTS_QUERY_PARAM, APPOINTMENTS_QUERY_OPEN);
            next.set(APPOINTMENT_QUERY_PARAM, appointmentId);
            next.delete(APPOINTMENT_PATIENT_QUERY_PARAM);
            return next;
        });
    };

    const upcoming = useMemo(() => {
        if (!patientId) return [];
        return appointments
            .filter(
                (appointment) =>
                    appointment.patientId === patientId &&
                    !appointment.canceledAt &&
                    appointment.date >= todayIso
            )
            .sort(
                (a, b) => a.date.localeCompare(b.date) || (a.time ?? '').localeCompare(b.time ?? '')
            )
            .slice(0, MAX_UPCOMING_APPOINTMENTS);
    }, [appointments, patientId, todayIso]);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 h-full min-h-0 flex flex-col overflow-hidden gap-3">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 shrink-0">
                Upcoming appointments
            </span>

            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-subtle">
                {isLoading ? (
                    <div className="flex flex-col gap-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3" />
                    </div>
                ) : upcoming.length === 0 ? (
                    <EmptyState />
                ) : (
                    <div className="flex flex-col gap-2">
                        {upcoming.map((appointment) => (
                            <AppointmentRow
                                key={appointment.id}
                                appointment={appointment}
                                todayIso={todayIso}
                                onSelect={openExistingAppointment}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export { UpcomingAppointmentsCard };
